import { isRef, nextTick } from 'vue';
import type { I18n } from 'vue-i18n';
import type { Router } from 'vue-router';

/** Matches GlobalSEO default `data-lang-parameter` when unset. */
export const COMET_LANG_QUERY = 'lang';

/** Persisted when the user picks a language (aligned with `public/language-bootstrap.js`). */
export const USER_SELECTED_LANGUAGE_KEY = 'user-selected-language';

export function parseAllowedLanguageCodesFromEnv(): string[] {
  const envAllowed = import.meta.env.VITE_GLOBALSEO_ALLOWED_LANGUAGES;
  if (typeof envAllowed !== 'string' || !envAllowed.trim()) {
    return ['en'];
  }
  return envAllowed
    .split(',')
    .map(l => l.trim().toLowerCase())
    .filter(Boolean);
}

export function normalizeToAllowedLocale(
  raw: string | null | undefined,
  allowed: string[]
): string | null {
  if (!raw || allowed.length === 0) {
    return null;
  }
  const lower = String(raw).trim().toLowerCase();
  if (allowed.includes(lower)) {
    return lower;
  }
  const prefix = lower.split('-')[0];
  const match = allowed.find(a => a.split('-')[0] === prefix);
  return match ?? null;
}

export function detectBrowserLocale(allowed: string[]): string | null {
  if (typeof navigator === 'undefined' || allowed.length === 0) {
    return null;
  }
  const langs = navigator.languages?.length
    ? [...navigator.languages]
    : navigator.language
    ? [navigator.language]
    : [];
  for (const lang of langs) {
    const resolved = normalizeToAllowedLocale(lang, allowed);
    if (resolved) {
      return resolved;
    }
  }
  return null;
}

export function setVueI18nLocale(i18n: I18n, code: string): void {
  const g = i18n.global as unknown as { locale: unknown };
  if (isRef(g.locale)) {
    (g.locale as { value: string }).value = code;
  } else {
    (g as { locale: string }).locale = code;
  }
}

function persistUserLanguage(code: string): void {
  try {
    localStorage.setItem(USER_SELECTED_LANGUAGE_KEY, code);
    localStorage.setItem('language', code);
  } catch {
    console.error('Error persisting user language:', code);
  }
}

function getGlobalSeoScriptTag(): HTMLScriptElement | null {
  if (window.translationScriptTag) {
    return window.translationScriptTag;
  }

  const el = document.querySelector<HTMLScriptElement>(
    'script[data-globalseo-key], script[data-weploy-key]'
  );
  if (el) {
    window.translationScriptTag = el;
  }
  return el;
}

/** Align runtime language state GlobalSEO reads during translation cycles. */
export function applyGlobalSeoLanguageState(resolved: string): void {
  window.paramsLang = resolved;
  window.globalseoActiveLang = resolved;
}

/**
 * Re-runs GlobalSEO after an in-app locale change. Initial page load is handled by
 * `translate.js`; vue-i18n updates alone do not re-translate dynamic DOM (e.g. custom fields).
 */
export async function syncGlobalSeoLocale(resolved: string): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  applyGlobalSeoLanguageState(resolved);

  const utils = window.globalseoUtils;
  if (!utils?.getTranslations || !utils.extractOptionsFromScript) {
    return;
  }

  const scriptTag = getGlobalSeoScriptTag();
  if (!scriptTag) {
    return;
  }

  const options = utils.extractOptionsFromScript(window, { activeLanguage: resolved });
  const apiKey =
    (typeof options.apiKey === 'string' && options.apiKey) ||
    scriptTag.getAttribute('data-globalseo-key') ||
    scriptTag.getAttribute('data-weploy-key') ||
    import.meta.env.VITE_GLOBALSEO_KEY;

  if (!apiKey) {
    return;
  }

  const opts = { ...options, activeLanguage: resolved, paramsLang: resolved };
  utils.setOptions?.(window, apiKey, opts);
  await utils.getTranslations(window, apiKey, opts);
}

/**
 * Applies locale from URL → persisted choice → browser → `en`, then ensures `?lang=` is set
 * for GlobalSEO `searchParams` mode.
 */
export async function applyInitialCometLocale(router: Router, i18n: I18n): Promise<void> {
  const allowed = parseAllowedLanguageCodesFromEnv();
  const rawLang = router.currentRoute.value.query[COMET_LANG_QUERY];
  const fromQuery = normalizeToAllowedLocale(
    Array.isArray(rawLang) ? rawLang[0] : rawLang,
    allowed
  );
  let fromStorage: string | null = null;
  try {
    fromStorage = normalizeToAllowedLocale(
      localStorage.getItem(USER_SELECTED_LANGUAGE_KEY),
      allowed
    );
  } catch {
    fromStorage = null;
  }
  const fromBrowser = detectBrowserLocale(allowed);
  const resolved =
    fromQuery ?? fromStorage ?? fromBrowser ?? (allowed.includes('en') ? 'en' : allowed[0] ?? 'en');

  setVueI18nLocale(i18n, resolved);
  document.documentElement.setAttribute('lang', resolved);

  const currentLang = router.currentRoute.value.query[COMET_LANG_QUERY];
  const currentStr = Array.isArray(currentLang) ? currentLang[0] : currentLang;
  if (String(currentStr || '').toLowerCase() !== resolved) {
    await router.replace({
      query: { ...router.currentRoute.value.query, [COMET_LANG_QUERY]: resolved }
    });
  }
}

export async function setCometLocale(router: Router, i18n: I18n, code: string): Promise<void> {
  const allowed = parseAllowedLanguageCodesFromEnv();
  const resolved = normalizeToAllowedLocale(code, allowed) ?? 'en';
  persistUserLanguage(resolved);
  applyGlobalSeoLanguageState(resolved);
  setVueI18nLocale(i18n, resolved);
  document.documentElement.setAttribute('lang', resolved);
  await router.replace({
    query: { ...router.currentRoute.value.query, [COMET_LANG_QUERY]: resolved }
  });
  // Remount locale-keyed custom fields back to API source strings before re-translating.
  await nextTick();
  await syncGlobalSeoLocale(resolved);
}

export function getLanguageOptions(
  allowed: string[],
  titleFor: (code: string) => string
): { value: string; title: string }[] {
  return allowed.map(value => ({
    value,
    title: titleFor(value)
  }));
}
